import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import ConsultationModal from '@/components/ConsultationModal';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Link } from 'react-router-dom';
import { useAdminEdit } from '@/contexts/AdminEditContext';
const Navigation = () => {
  const { t } = useLanguage();
  const { isAdmin, editMode, setEditMode } = useAdminEdit();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    // If not on home page, navigate to home first then scroll
    if (window.location.pathname !== '/') {
      // Store the target section for scrolling after navigation
      sessionStorage.setItem('scrollTarget', href);
      window.location.href = '/';
    } else {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Handle scrolling to target section after navigation from other pages
  useEffect(() => {
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget && window.location.pathname === '/') {
      sessionStorage.removeItem('scrollTarget');
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        const target = document.querySelector(scrollTarget);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const navItems = [
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.contact'), href: '#references' },
    { name: t('nav.joinUs'), href: '#joinus' }
  ];

  const serviceItems = [
    { name: t('services.aiTitle'), href: '/services/ai-solutions' },
    { name: t('services.webTitle'), href: '/services/web-seo' },
    { name: t('services.itTitle'), href: '/services/it-consulting' },
    { name: t('services.softwareTitle'), href: '/services/software-development' },
    { name: t('services.socialTitle'), href: '/services/automate-workflows' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-smooth ${
      isScrolled 
        ? 'bg-background/80 backdrop-blur-lg border-b border-border' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-display font-bold gradient-text hover:opacity-80 transition-smooth">
              commodIT
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className="text-foreground hover:text-primary transition-smooth font-medium"
              onClick={() => window.scrollTo(0, 0)}
            >
              {t('nav.home')}
            </Link>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary">
                    {t('nav.services')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[400px] gap-3 p-4 bg-background/95 backdrop-blur-lg border border-border">
                      {serviceItems.map((service) => (
                        <NavigationMenuLink key={service.name} asChild>
                          <Link
                            to={service.href}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            onClick={() => window.scrollTo(0, 0)}
                          >
                            <div className="text-sm font-medium leading-none">{service.name}</div>
                          </Link>
                        </NavigationMenuLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleSmoothScroll(e, item.href)}
                className="text-foreground hover:text-primary transition-smooth font-medium"
              >
                {item.name}
              </a>
            ))}
            <LanguageSelector />
            {isAdmin && (
              <>
                <Button
                  variant={editMode ? "secondary" : "outline"}
                  onClick={() => setEditMode(!editMode)}
                  className="border-primary/40"
                >
                  {editMode ? 'Editing…' : 'Edit'}
                </Button>
                {editMode && (
                  <Link to="/portal-commodit-7842">
                    <Button variant="outline" size="sm">
                      Back to Admin
                    </Button>
                  </Link>
                )}
              </>
            )}
            <Button 
              variant="default" 
              className="gradient-primary glow-primary transition-spring hover:scale-105"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('common.consultation')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 space-y-4 bg-background/95 backdrop-blur-lg border-b border-border">
            <Link
              to="/"
              className="block text-foreground hover:text-primary transition-smooth font-medium py-2"
              onClick={() => {
                setIsMobileMenuOpen(false);
                window.scrollTo(0, 0);
              }}
            >
              {t('nav.home')}
            </Link>
            
            {/* Services Dropdown for Mobile */}
            <div className="space-y-2">
              <div className="text-foreground font-medium py-2 px-2">{t('nav.services')}</div>
              {serviceItems.map((service) => (
                <Link
                  key={service.name}
                  to={service.href}
                  className="block text-muted-foreground hover:text-primary transition-smooth font-medium py-2 pl-6"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.scrollTo(0, 0);
                  }}
                >
                  {service.name}
                </Link>
              ))}
            </div>
            
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block text-foreground hover:text-primary transition-smooth font-medium py-2"
                onClick={(e) => {
                  handleSmoothScroll(e, item.href);
                  setIsMobileMenuOpen(false);
                }}
              >
                {item.name}
              </a>
            ))}
            
            <div className="py-2">
              <LanguageSelector />
            </div>
            
            {isAdmin && editMode && (
              <Link to="/portal-commodit-7842">
                <Button variant="outline" className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                  Back to Admin
                </Button>
              </Link>
            )}
            
            <Button 
              variant="default" 
              className="w-full gradient-primary"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('common.consultation')}
            </Button>
          </div>
        )}
      </div>

      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </nav>
  );
};

export default Navigation;